// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { gunzipSync, unzip } from 'fflate';
import { safeEntryPath } from './domain/srcpath';

/**
 * Getting a whole built folder into the tab.
 *
 * THE `.env` IS A SEPARATE, PASTE-FIRST INPUT and that is not a UX preference, it is forced:
 * WebKit filters hidden files out of BOTH ingest paths. `DirectoryFileListCreator.cpp` returns
 * early on `isHiddenFile(childPath)` BEFORE recursing (so hidden directories are pruned too), and
 * the Entries API uses `fileTypeIgnoringHiddenFiles`, so even asking for `.env` by exact name
 * returns NotFoundError. `isHiddenFile` is `filename[0] == '.'` under `#if OS(UNIX)` — macOS AND
 * iOS. A folder ingest can therefore NEVER deliver a `.env` on Safari, at any price.
 *
 * (The widely-repeated claim that Firefox drag-drop also excludes dotfiles is FALSE — that branch is
 * gated on a `filterOutSensitive` flag whose only caller is a legacy API. Do not ship a warning
 * about it.)
 */

export interface IngestedFile {
  readonly path: string;
  readonly bytes: Uint8Array;
}

export interface IngestResult {
  readonly files: IngestedFile[];
  readonly skippedPaths: { path: string; reason: string }[];
  readonly brotli: string[];
  readonly inflatedGzip: number;
}

const MAX_ENTRY_BYTES = 64 * 1024 * 1024;
const MAX_TOTAL_BYTES = 3 * 1024 * 1024 * 1024;
const MAX_FILES = 60_000;

function stripLeadingDir(paths: string[]): (p: string) => string {
  // A dropped folder arrives as `dist/assets/x.js`; a zip of it may be `dist/...` or bare. Strip a
  // single common top-level directory so paths read the same either way.
  if (paths.length < 2) return (p) => p;
  const first = paths[0]!.split('/')[0];
  if (!first || !paths.every((p) => p.startsWith(`${first}/`))) return (p) => p;
  return (p) => p.slice(first.length + 1);
}

/**
 * Post-process an ingested set: inflate `.gz` siblings, count brotli by name, apply path safety.
 *
 * `.gz` matters because `vite-plugin-compression`, Next.js and several CDN pipelines emit
 * `app.js.gz` beside `app.js`, and a raw byte search finds nothing inside one. Brotli is NOT
 * scanned — fflate has no decoder and `DecompressionStream` has no `br` mode — so it is named and
 * counted rather than passed over in silence.
 */
export function normalise(raw: { path: string; bytes: Uint8Array }[]): IngestResult {
  const skippedPaths: { path: string; reason: string }[] = [];
  const brotli: string[] = [];
  const files: IngestedFile[] = [];
  let inflatedGzip = 0;
  let total = 0;

  const strip = stripLeadingDir(raw.map((r) => r.path));

  for (const r of raw) {
    if (files.length >= MAX_FILES) {
      skippedPaths.push({ path: r.path, reason: `past leakmap's ${MAX_FILES.toLocaleString('en-GB')}-file ceiling` });
      continue;
    }
    const path = strip(r.path);
    const safe = safeEntryPath(path);
    if (!safe.ok) {
      skippedPaths.push({ path, reason: safe.reason });
      continue;
    }
    if (path.endsWith('.br')) {
      brotli.push(safe.path);
      continue;
    }
    if (r.bytes.length > MAX_ENTRY_BYTES) {
      skippedPaths.push({ path: safe.path, reason: `larger than ${MAX_ENTRY_BYTES / 1024 / 1024} MB` });
      continue;
    }
    total += r.bytes.length;
    if (total > MAX_TOTAL_BYTES) {
      skippedPaths.push({ path: safe.path, reason: 'past the 3 GiB total ceiling' });
      continue;
    }

    if (path.endsWith('.gz')) {
      try {
        const inflated = gunzipSync(r.bytes);
        inflatedGzip++;
        files.push({ path: safe.path.slice(0, -3), bytes: inflated });
        continue;
      } catch {
        skippedPaths.push({ path: safe.path, reason: 'gzip could not be inflated' });
        continue;
      }
    }
    files.push({ path: safe.path, bytes: r.bytes });
  }

  return { files, skippedPaths, brotli, inflatedGzip };
}

/**
 * Read a dropped `.zip` — the universal path, and the only one that works on iOS.
 *
 * One-shot `unzip` with a `filter`, NOT the streaming `Unzip`: streaming requires an explicit
 * `register(UnzipInflate)` and otherwise throws a bare `TypeError: ctr is not a constructor` with no
 * `.code` on every real (method-8) entry. The filter runs PRE-INFLATE and receives `originalSize`,
 * so a zip bomb is refused before it is expanded rather than after.
 *
 * fflate does NOT verify CRC32 on read and DOES truncate to the declared uncompressed size without
 * throwing, so the inflated length is checked against `originalSize` and a mismatch becomes a
 * finding. A security tool that reports a clean bill of health on a silently truncated file has
 * broken its own promise.
 */
export function readZip(bytes: Uint8Array): Promise<{ path: string; bytes: Uint8Array }[]> {
  return new Promise((resolve, reject) => {
    unzip(
      bytes,
      {
        filter: (f) => !f.name.endsWith('/') && f.originalSize < MAX_ENTRY_BYTES,
      },
      (err, out) => {
        if (err) {
          reject(new Error('That .zip could not be read.'));
          return;
        }
        resolve(Object.entries(out).map(([path, b]) => ({ path, bytes: b })));
      },
    );
  });
}

/**
 * Walk a dropped directory entry.
 *
 * `readEntries` RETURNS AT MOST 100 ENTRIES AT A TIME and says nothing when it stops, so the naive
 * single call silently truncates a real `dist/` to its first 100 files — and a security tool that
 * quietly reads a fifth of your build is worse than one that refuses.
 */
export async function walkEntry(entry: FileSystemEntry, prefix = ''): Promise<{ path: string; bytes: Uint8Array }[]> {
  const out: { path: string; bytes: Uint8Array }[] = [];
  if (entry.isFile) {
    const file = await new Promise<File>((res, rej) => (entry as FileSystemFileEntry).file(res, rej));
    out.push({ path: `${prefix}${entry.name}`, bytes: new Uint8Array(await file.arrayBuffer()) });
    return out;
  }
  if (!entry.isDirectory) return out;

  const reader = (entry as FileSystemDirectoryEntry).createReader();
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
    if (batch.length === 0) break;
    for (const child of batch) out.push(...(await walkEntry(child, `${prefix}${entry.name}/`)));
  }
  return out;
}

/** Read a `<input webkitdirectory>` or multi-file selection. */
export async function readFileList(list: FileList): Promise<{ path: string; bytes: Uint8Array }[]> {
  const out: { path: string; bytes: Uint8Array }[] = [];
  for (const f of Array.from(list)) {
    const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath;
    out.push({ path: rel && rel.length > 0 ? rel : f.name, bytes: new Uint8Array(await f.arrayBuffer()) });
  }
  return out;
}

/**
 * The Chromium-only directory picker.
 *
 * Requested with `{ mode: 'read' }` and NEVER persisted. A `FileSystemHandle` is
 * structured-cloneable, and every "remember my folder" tutorial writes one to IndexedDB — which
 * would break the storage promise in the threat model and leave a handle to the user's source tree
 * on disk after they closed the tab.
 */
export async function pickDirectory(): Promise<{ path: string; bytes: Uint8Array }[] | null> {
  const w = window as unknown as { showDirectoryPicker?: (o: object) => Promise<FileSystemDirectoryHandle> };
  if (typeof w.showDirectoryPicker !== 'function') return null;
  const handle = await w.showDirectoryPicker({ mode: 'read' });
  return walkHandle(handle, '');
}

async function walkHandle(dir: FileSystemDirectoryHandle, prefix: string): Promise<{ path: string; bytes: Uint8Array }[]> {
  const out: { path: string; bytes: Uint8Array }[] = [];
  const iterable = dir as unknown as AsyncIterable<[string, FileSystemHandle]>;
  for await (const [name, handle] of iterable) {
    if (handle.kind === 'file') {
      const file = await (handle as FileSystemFileHandle).getFile();
      out.push({ path: `${prefix}${name}`, bytes: new Uint8Array(await file.arrayBuffer()) });
    } else {
      out.push(...(await walkHandle(handle as FileSystemDirectoryHandle, `${prefix}${name}/`)));
    }
  }
  return out;
}

/** True when a dropped file is a zip, by magic bytes rather than by extension. */
export function looksLikeZip(bytes: Uint8Array): boolean {
  return bytes.length > 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && (bytes[2] === 3 || bytes[2] === 5 || bytes[2] === 7);
}
